import { compareEntityNames } from "@shared/connectors/entityOrder";
import {
  SkyBadge,
  SkyButton,
  SkyDataGrid,
  SkyLink,
  SkyPopover,
  SkyText,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import { useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { Link } from "wouter";
import { pixelColumnLayout } from "@/lib/grid";
import { usePinnedColumnEdge } from "@/lib/gridPinned";
import { absolutePath, connectionPath } from "@/lib/routes";
import type { ImportCauseView } from "@shared/types/importCauses";
import type {
  EntityProgressRow,
  EntitySchemaChanges,
  RunEntityStatus,
  RunProgressNotification,
} from "@shared/types/runs";
import { entityKey } from "@/hooks/use-run-progress";
import { NOW_TICK_MS, useNow } from "@/hooks/use-now";
import { ValidationWarningIcon } from "@/lib/icons";
import {
  ENTITY_PAGE_SIZE,
  ENTITY_PAGE_SIZE_OPTIONS,
  ENTITY_STAGE_FILTER_OPTIONS,
  entityStagePresentation,
  formatCount,
  formatDuration,
  formatStageSeconds,
  LIVE_LOAD_ATTEMPT,
  notReadYet,
  withEntityStage,
  type EntityStageRow,
} from "./runFormatting";
import { useReportingConnections } from "./useReportingConnections";
import { ImportCauseBlock, type ImportCauseHandlers } from "./ImportCauseBlock";
import { StageBadge } from "./StageBadge";
import { StatusCell } from "./StatusCell";
import { BREAKABLE_NAME_CLASS, breakableName } from "@/lib/breakableName";

/**
 * Rows per table (FR-55, UX-5; Session 7, O1): what each table extracted, what the manifest
 * expected, what the load wrote, and how long each stage took.
 *
 * The rows come from `GET /runs/:runId`, which is the record: the server merges the extract's
 * `entity_progress` rows with the load step's per-table outcome. The WebSocket feed is merged
 * over them, which is the hint: a notification updates ONE row without refetching the run, and
 * a notification for a row the fetch has not returned yet is added rather than dropped, because
 * the first entity to report often reports before the next poll.
 *
 * A notification never lowers a row. `succeeded` from the database is not replaced by a later
 * `running` message that overtook it on the way here: `NOTIFY` has no ordering guarantee across
 * sessions, and a finished entity flickering back to "Loading" is the kind of thing an operator
 * escalates. A notification carries extract progress only, so the load's outcome the fetch
 * attached is always kept.
 *
 * Two rules for the numbers. A count nobody reported is a dash with its reason, never a zero:
 * zero means the source had no rows. And the page states a mismatch, it does not judge it: a
 * loaded count that differs from Expected has already failed that table in the runner (BL-4),
 * so the cell says how far off it is and the status says what the runner decided.
 */

/** Terminal states cannot be walked back by a late message. */
const SETTLED: ReadonlySet<RunEntityStatus> = new Set(["succeeded", "failed", "skipped"]);

export function mergeEntityProgress(
  rows: EntityProgressRow[],
  live: ReadonlyMap<string, RunProgressNotification>,
): EntityProgressRow[] {
  const merged = new Map<string, EntityProgressRow>();
  for (const row of rows) merged.set(entityKey(row.dataflowId, row.entityName), row);

  for (const [key, notification] of live) {
    if (!notification.entityName) continue;
    const existing = merged.get(key);
    if (!existing) {
      merged.set(key, {
        dataflowId: notification.dataflowId ?? "",
        entityName: notification.entityName,
        status: notification.status ?? "running",
        records: notification.records ?? 0,
        pages: 0,
        startedAt: notification.at,
        finishedAt: null,
        error: notification.error,
      });
      continue;
    }
    if (SETTLED.has(existing.status)) continue;
    // `...existing` keeps what only the fetch knows: the load's outcome, `extractReported`
    // and the extract's duration.
    merged.set(key, {
      ...existing,
      // #1127: a planned (waiting) table that sends any message has started, as a new row has.
      status: notification.status ?? (existing.status === "waiting" ? "running" : existing.status),
      // A live count that is behind the stored one is a message that overtook
      // another; keep the larger rather than counting backwards on screen.
      records: Math.max(existing.records, notification.records ?? 0),
      error: notification.error ?? existing.error,
      // #1127: a planned (waiting) row starts when its first message arrives.
      startedAt: existing.startedAt ?? notification.at,
      finishedAt:
        notification.status && SETTLED.has(notification.status)
          ? notification.at
          : existing.finishedAt,
    });
  }

  // #1149: the connection's own table order, as the server's grid sends it.
  return [...merged.values()].sort((a, b) => compareEntityNames(a.entityName, b.entityName));
}

/** "4 fewer than expected" / "2 more than expected". */
function offBy(actual: number, expected: number): string {
  const difference = Math.abs(expected - actual);
  return `${formatCount(difference)} ${actual < expected ? "fewer" : "more"} than expected`;
}

/** A count and, under it, the reason or the delta. The number stays in its own element. */
function CountCell({ value, note }: { value: ReactNode; note?: string }) {
  return (
    <div className="flex flex-col items-end">
      <SkyText size={200} font="numeric">
        {value}
      </SkyText>
      {note ? (
        <SkyText size={200} color="muted" block>
          {note}
        </SkyText>
      ) : null}
    </div>
  );
}

function extractedCell(row: EntityProgressRow, reportingRows: ReadonlySet<string>) {
  if (row.extractReported === false) return <CountCell value="—" note="not reported" />;
  // #2389: the badge reads "Waiting to read", so the count must not read "0" (read, and empty).
  if (notReadYet(row, reportingRows)) return <CountCell value="—" note="not read yet" />;
  if (row.records === 0) {
    if (row.status === "failed") return <CountCell value="—" note="failed before a count" />;
    if (row.status === "skipped") return <CountCell value="—" note="skipped" />;
    // #1127: a planned table that has not reported yet has not started either.
    if (row.status === "pending" || row.status === "waiting") {
      return <CountCell value="—" note="not started" />;
    }
  }
  const expected = row.load?.expectedRows ?? null;
  if (row.status === "succeeded" && expected !== null && row.records !== expected) {
    return (
      <div
        className="-my-1 rounded-sm bg-warning-subtle px-2 py-1"
        data-testid={`entity-extract-mismatch-${row.entityName}`}
      >
        <CountCell value={formatCount(row.records)} note={offBy(row.records, expected)} />
      </div>
    );
  }
  return <CountCell value={formatCount(row.records)} />;
}

/** Why a table has no load outcome, as far as the grid can tell. */
function noLoadNote(runTerminal: boolean | undefined): string {
  if (runTerminal === undefined) return "not loaded";
  return runTerminal ? "load did not run" : "not loaded yet";
}

function loadedCell(
  row: EntityProgressRow,
  runTerminal: boolean | undefined,
  noDestination: boolean,
) {
  const load = row.load;
  if (!load) {
    if (row.status === "failed" || row.status === "skipped") return <CountCell value="—" />;
    return <CountCell value="—" note={noLoadNote(runTerminal)} />;
  }
  // #2392: a table the source sent nothing for loaded 0 rows, a real count.
  if (load.noRows) return <CountCell value={formatCount(0)} />;
  // A skip reads the same live and recorded: "Not loaded", and why only for no destination.
  if (load.status === "skipped") {
    return noDestination ? (
      <CountCell value="Not loaded" note="no destination" />
    ) : (
      <CountCell value="Not loaded" />
    );
  }
  // #1148: an outcome seen live, before the load step ends and the record attaches its counts.
  if (load.attempt === LIVE_LOAD_ATTEMPT) {
    return load.status === "succeeded" ? (
      <CountCell value="—" note="counted when the load ends" />
    ) : (
      <CountCell value="—" />
    );
  }
  if (load.rowsLoaded === null) {
    return (
      <CountCell
        value="—"
        note={load.status === "failed" ? "failed before a count" : "not reported"}
      />
    );
  }
  const duplicates = duplicatesNote(load);
  if (load.expectedRows !== null && load.rowsLoaded !== load.expectedRows) {
    // Colour is never the only signal: the stripe, the icon and the words carry it too.
    return (
      <div
        className="-my-1 flex items-start justify-end gap-1 rounded-sm border-l-4 border-destructive bg-destructive-subtle px-2 py-1"
        data-testid={`entity-load-mismatch-${row.entityName}`}
      >
        <ValidationWarningIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <div className="flex flex-col items-end">
          <SkyText size={200} font="numeric" weight="semibold">
            {formatCount(load.rowsLoaded)}
          </SkyText>
          <SkyText size={200} color="danger" block>
            {offBy(load.rowsLoaded, load.expectedRows)}
          </SkyText>
          {duplicates !== undefined && (
            <SkyText size={200} color="muted" block>
              {duplicates}
            </SkyText>
          )}
        </div>
      </div>
    );
  }
  return <CountCell value={formatCount(load.rowsLoaded)} note={duplicates} />;
}

/**
 * Product gaps row 22: the incremental MERGE keeps the latest row per key, and the load counts
 * what it left out. Said only when it left something out; the loaded number stays the COPY's.
 */
function duplicatesNote(load: NonNullable<EntityProgressRow["load"]>): string | undefined {
  const dropped = load.duplicateRowsDropped;
  if (dropped === undefined || dropped <= 0) return undefined;
  return `${formatCount(dropped)} duplicate ${dropped === 1 ? "row" : "rows"} dropped`;
}

const RUNNING_TICK_MS = 5_000;

/** A SQL Server connection can hold 6,000 tables: 100 rows reach the DOM at a time (#1465). */

function extractDuration(
  row: EntityProgressRow,
  runTerminal: boolean | undefined,
  nowMs: number,
): string {
  if (row.durationSeconds !== undefined) return formatStageSeconds(row.durationSeconds);
  if (!row.startedAt) return "—";
  const start = Date.parse(row.startedAt);
  if (row.finishedAt) return formatStageSeconds((Date.parse(row.finishedAt) - start) / 1000);
  // Still running: count up while the run moves, never on a run that has stopped.
  return runTerminal === false ? formatDuration((nowMs - start) / 1000) : "—";
}

/**
 * Product gaps row 4 (design §4.7): a failed table's cause as its label badge under the status;
 * pressing it opens the cause block in a popover, anchored to the row. No column of its own, so
 * the grid's widths hold at 1024 px.
 */
function TableCause({
  row,
  cause,
  handlers,
}: {
  row: EntityProgressRow;
  cause: ImportCauseView;
  handlers?: ImportCauseHandlers;
}) {
  const [open, setOpen] = useState(false);
  return (
    <SkyPopover
      // SkyPopover forwards no aria props: the dialog is named through its ref (NFR-7).
      ref={(element: HTMLElement | null) =>
        element?.setAttribute("aria-label", `Cause for ${row.entityName}`)
      }
      open={open}
      onOpenChange={setOpen}
      placement="bottom-start"
      className="max-w-sm p-3"
      testId={`entity-cause-popover-${row.entityName}`}
      trigger={
        <button
          type="button"
          className="self-start rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-expanded={open}
          aria-label={`Cause for ${row.entityName}: ${cause.label}`}
          onClick={() => setOpen((value) => !value)}
          data-testid={`entity-cause-${row.entityName}`}
        >
          <SkyBadge appearance="outline" color="danger">
            {cause.label}
          </SkyBadge>
        </button>
      }
    >
      <ImportCauseBlock
        cause={cause}
        compact
        action={handlers ? handlers.action(cause, row.dataflowId) : null}
        permitted={handlers ? handlers.permitted(cause) : false}
        {...(handlers?.askLine ? { askLine: handlers.askLine(cause) } : {})}
        testId={`entity-cause-block-${row.entityName}`}
      />
    </SkyPopover>
  );
}

/**
 * Round 6 (T-02): the SQL extractor v1.7.0's read summary of the table, under its name. Rows read
 * and read time always; retries only when there were any; slices only for a partitioned read.
 */
function ReadSummary({ row }: { row: EntityProgressRow }) {
  const read = row.read;
  if (!read) return null;
  const parts = [
    `Rows read ${formatCount(read.rowsRead)}`,
    `Read time ${formatStageSeconds(read.readSeconds)}`,
    ...(read.retries > 0 ? [`Retries ${formatCount(read.retries)}`] : []),
    ...(read.slices !== null ? [`Read in ${formatCount(read.slices)} slices`] : []),
  ];
  return (
    <SkyText size={200} color="muted" block testId={`entity-read-summary-${row.entityName}`}>
      {parts.join(" · ")}
    </SkyText>
  );
}

function counted(count: number, singular: string, plural: string, verb: string): string[] {
  return count > 0 ? [`${formatCount(count)} ${count === 1 ? singular : plural} ${verb}`] : [];
}

/** #1663: the words of a table's column changes in one run, in the order the log's kinds go. */
export function schemaChangeParts(changes: EntitySchemaChanges): string[] {
  const n = (count: number) => formatCount(count);
  return [
    ...(changes.added > 0 ? [`${n(changes.added)} added`] : []),
    ...((changes.held ?? 0) > 0 ? [`${n(changes.held)} waiting for review`] : []),
    ...(changes.notSent > 0 ? [`${n(changes.notSent)} not sent and kept`] : []),
    ...(changes.typeChanged > 0
      ? [`${n(changes.typeChanged)} ${changes.typeChanged === 1 ? "type" : "types"} changed`]
      : []),
    ...(changes.keyLost > 0
      ? [`${n(changes.keyLost)} key ${changes.keyLost === 1 ? "column" : "columns"} gone`]
      : []),
  ];
}

/**
 * #1663: the table's column changes from this import (the log's rows first seen in this run),
 * with a link that opens that table's changes on its connection, on "This import". It replaces the
 * extractor's own line when it shows, because the log already holds the extractor's changes of a table that landed. A table a change stopped never logged them, so its source line stays beside it.
 */
function RunSchemaChange({ row, runId }: { row: EntityProgressRow; runId?: string }) {
  const parts = row.schemaChanges ? schemaChangeParts(row.schemaChanges) : [];
  if (parts.length === 0) return null;
  const runParam = runId ? `&run=${encodeURIComponent(runId)}` : "";
  const href = `${connectionPath(row.dataflowId)}?schemaChanges=${encodeURIComponent(row.entityName)}${runParam}`;
  return (
    <SkyText size={200} block testId={`entity-schema-changes-${row.entityName}`}>
      Columns changed: {parts.join(", ")}.{" "}
      <SkyLink
        as={Link}
        href={absolutePath(href)}
        title="Opens this table's changes from this import."
        testId={`entity-schema-changes-link-${row.entityName}`}
      >
        View changes
      </SkyLink>
    </SkyText>
  );
}

/**
 * Round 6 (T-02): one line when the extractor saw the table's source schema change, linking to that
 * table's log on its connection, where each change is listed and any decision is made.
 */
function SourceSchemaChange({ row }: { row: EntityProgressRow }) {
  const changes = row.load?.sourceSchemaChanges;
  if (!changes) return null;
  const parts = [
    ...counted(changes.added, "column", "columns", "added"),
    ...counted(changes.dropped, "column", "columns", "not sent"),
    ...counted(changes.typeChanged, "type", "types", "changed"),
  ];
  if (parts.length === 0) return null;
  const href = `${connectionPath(row.dataflowId)}?schemaChanges=${encodeURIComponent(row.entityName)}`;
  return (
    <SkyLink as={Link} href={absolutePath(href)} testId={`entity-source-schema-${row.entityName}`}>
      <SkyText size={200} block>
        Schema changed at source: {parts.join(", ")}
      </SkyText>
    </SkyLink>
  );
}

export interface EntityProgressGridProps {
  /** #1663: the run these rows belong to; a table's changes link opens that import's changes. */
  runId?: string;
  rows: EntityProgressRow[];
  live: ReadonlyMap<string, RunProgressNotification>;
  connectionNames: Map<string, string>;
  isLoading: boolean;
  /**
   * True once the run can no longer change. It tells a load that is still to come ("Loading")
   * from one that never ran ("Extracted, not loaded"). Omitted, the grid can only infer that a
   * table still waiting or extracting means the run is moving. When no table is, it cannot tell
   * a load that is running from one that never ran, so an extracted table without a load reads
   * a neutral "Extracted" / "not loaded" rather than a claim that may be false.
   */
  runTerminal?: boolean;
  /** True when the run's load said it had no destination (`loadHadNoDestination`). */
  noDestination?: boolean;
  /** #1651: connections whose load waits for an import slot (`loadsWaitingForSlot`). */
  waitingLoads?: ReadonlySet<string>;
  /** #1672: connections whose load is sending the tables' files (`loadsSendingFiles`). */
  sendingLoads?: ReadonlySet<string>;
  /**
   * The run log's row action (Session 10, P11): "Show events" sets the Events view's Table filter
   * to this table. Omitted, the grid has no action column.
   */
  onShowEvents?: (row: EntityProgressRow) => void;
  /** Product gaps row 4: the page's action for a table's cause. Absent, the fix alone. */
  causes?: ImportCauseHandlers;
}

/** Under a table's status: its cause badge (with the error under it), else the error alone. */
function statusSecondary(
  row: EntityProgressRow,
  error: string | null | undefined,
  causes: ImportCauseHandlers | undefined,
): ReactNode {
  if (row.cause) {
    return (
      <div className="flex min-w-0 flex-col gap-1">
        <TableCause row={row} cause={row.cause} handlers={causes} />
        {error ? (
          <SkyText size={200} color="danger" block>
            {error}
          </SkyText>
        ) : null}
      </div>
    );
  }
  if (error) {
    return (
      <SkyText size={200} color="danger" block>
        {error}
      </SkyText>
    );
  }
  return undefined;
}

/** #1173: each column's least width in px (header wrapped at its space, plus 32 px of padding). */
const MINIMUMS = {
  entityName: 118,
  // #2392 (review): raised from 146 (measured for "Extracted, not loaded") as far as the 1024 px
  // pane's budget allows (`entityProgressGrid.floor.test.tsx`); the longer "No rows yet, table not
  // created" wraps inside it (`StageBadge`), and a wider pane gives the column more.
  stage: 158,
  records: 141,
  expected: 115,
  loaded: 96,
  extractTime: 98,
  loadTime: 76,
  actions: 118,
} as const;

export function EntityProgressGrid({
  runId,
  rows,
  live,
  connectionNames,
  isLoading,
  runTerminal: runTerminalProp,
  noDestination = false,
  waitingLoads,
  sendingLoads,
  onShowEvents,
  causes,
}: EntityProgressGridProps) {
  const merged = useMemo(() => mergeEntityProgress(rows, live), [rows, live]);
  // A run of several connections can hold one table name twice: its "Show events" names the
  // connection too, so no two buttons share an accessible name (review r2 nit).
  // Show events is the last column and the pane can be narrower than the floor (671 px at 768 px),
  // so it stays pinned, but only while nothing passes under it (`grid-unpin-scrolling`, #1143: the
  // floor is inside the 1024 px pane, so a few pixels of scroll must not hide Rows loaded); the hook attaches once, so only while the grid shows rows.
  const pinnedEdge = usePinnedColumnEdge<HTMLDivElement>(
    onShowEvents !== undefined && !isLoading && merged.length > 0,
  );
  const severalConnections = new Set(merged.map((row) => row.dataflowId)).size > 1;
  const runTerminal: boolean | undefined =
    runTerminalProp ??
    (merged.some(
      (row) => row.status === "pending" || row.status === "waiting" || row.status === "running",
    )
      ? false
      : undefined);
  // #1260: a running table's count-up moves on its own, every few seconds while the run moves.
  const now = useNow(runTerminal === false ? RUNNING_TICK_MS : NOW_TICK_MS);
  // #2389: the connections whose connector reports rows read as it goes ("Waiting to read"),
  // held for the rest of the run.
  const reportingRows = useReportingConnections(merged, runId);
  // The Status filter reads the stage the badge shows, so each row carries it.
  const data = useMemo(
    () =>
      withEntityStage(
        merged,
        runTerminal,
        noDestination,
        waitingLoads,
        sendingLoads,
        reportingRows,
      ),
    [merged, runTerminal, noDestination, waitingLoads, sendingLoads, reportingRows],
  );
  const [filtered, setFiltered] = useState(false);

  // #1173: pixel minimums for each header (wrapped at its space) and widest cell, summed into the
  // table's floor, so no header runs under its neighbour at 1024 px and a narrower pane scrolls.
  const columns: SkyDataGridColumn<EntityStageRow>[] = [
    {
      key: "entityName",
      header: "Table",
      width: "10%",
      sortable: true,
      renderCell: (_value, row) => (
        <div className="min-w-0">
          <SkyText size={300} weight="semibold" block truncate>
            {row.entityName}
          </SkyText>
          {row.load?.targetTable ? (
            // Walk 2026-10-02, D19: round 6 names are long ("MOUSEFLOW_SANDBOX_FUNNELS_STEPS"), so
            // the whole name shows, wrapping at its underscores, never cut.
            <SkyText
              size={200}
              color="muted"
              font="monospace"
              block
              className={BREAKABLE_NAME_CLASS}
              title={row.load.targetTable}
              testId={`entity-target-${row.entityName}`}
            >
              → {breakableName(row.load.targetTable)}
            </SkyText>
          ) : null}
          <SkyText size={200} color="muted" block truncate>
            {connectionNames.get(row.dataflowId) ?? "This connection"}
          </SkyText>
          <ReadSummary row={row} />
          {row.schemaChanges ? <RunSchemaChange row={row} runId={runId} /> : null}
          {!row.schemaChanges || row.schemaChanges.stopped > 0 ? (
            <SourceSchemaChange row={row} />
          ) : null}
        </div>
      ),
    },
    {
      key: "stage",
      header: "Status",
      width: "10%",
      // The grid filters the full list on the stage the badge shows (an extract or a load
      // failure is "Failed"), so a failed table is found on any page.
      filterable: true,
      filterConfig: { type: "select", options: [...ENTITY_STAGE_FILTER_OPTIONS] },
      renderCell: (_value, row) => {
        const presentation = entityStagePresentation(
          row,
          runTerminal,
          noDestination,
          waitingLoads,
          sendingLoads,
          reportingRows,
        );
        const error = row.load?.error ?? row.error;
        return (
          <StatusCell
            testId={`entity-status-${row.entityName}`}
            badge={<StageBadge presentation={presentation} />}
            secondary={statusSecondary(row, error, causes)}
          />
        );
      },
    },
    {
      key: "records",
      header: "Rows extracted",
      width: "10%",
      align: "right",
      sortable: true,
      renderCell: (_value, row) => extractedCell(row, reportingRows),
    },
    {
      key: "expected",
      header: "Expected",
      width: "10%",
      align: "right",
      renderCell: (_value, row) => <CountCell value={formatCount(row.load?.expectedRows)} />,
    },
    {
      key: "loaded",
      header: "Rows loaded",
      width: "10%",
      align: "right",
      renderCell: (_value, row) => loadedCell(row, runTerminal, noDestination),
    },
    {
      key: "extractTime",
      header: "Extract",
      width: "10%",
      align: "right",
      renderCell: (_value, row) => (
        <SkyText size={200} font="numeric">
          {extractDuration(row, runTerminal, now.getTime())}
        </SkyText>
      ),
    },
    {
      key: "loadTime",
      header: "Load",
      width: "10%",
      align: "right",
      renderCell: (_value, row) => (
        <SkyText size={200} font="numeric">
          {formatStageSeconds(row.load?.durationSeconds)}
        </SkyText>
      ),
    },
  ];
  if (onShowEvents) {
    columns.push({
      key: "actions",
      header: "Actions",
      width: "10%",
      renderCell: (_value, row) => (
        <SkyButton
          appearance="subtle"
          size="small"
          onClick={() => onShowEvents(row)}
          aria-label={
            severalConnections
              ? `Show events for ${row.entityName} (${connectionNames.get(row.dataflowId) ?? "this connection"})`
              : `Show events for ${row.entityName}`
          }
          testId={`entity-show-events-${row.entityName}`}
        >
          Show events
        </SkyButton>
      ),
    });
  }

  // The floor is the sum of the minimums of the columns shown (Actions only with Show events).
  const layout = pixelColumnLayout(columns, { minimums: MINIMUMS, selection: false });
  const floor = layout.floor;

  return (
    <div className="flex flex-col gap-2">
      <div ref={pinnedEdge} style={{ "--entity-grid-floor": `${floor}px` } as CSSProperties}>
        <SkyDataGrid<EntityStageRow>
          className={`${onShowEvents ? "grid-pinned-last grid-unpin-scrolling " : ""}[&_table]:min-w-(--entity-grid-floor) [&_th]:whitespace-normal`}
          data={data}
          columns={layout.columns}
          getRowId={(row) => entityKey(row.dataflowId, row.entityName)}
          loading={isLoading}
          pagination
          pageSize={ENTITY_PAGE_SIZE}
          pageSizeOptions={ENTITY_PAGE_SIZE_OPTIONS}
          onFiltersChange={(filters) =>
            setFiltered(
              Object.values(filters).some(
                (filter) =>
                  filter.value !== null &&
                  filter.value !== "" &&
                  !(Array.isArray(filter.value) && filter.value.length === 0),
              ),
            )
          }
          emptyMessage={
            // A filter that hides every row must not claim the run had none.
            filtered && data.length > 0
              ? "No table has this status."
              : // #1043: a finished run will report no more tables, so "yet" would promise rows.
                runTerminal === true
                ? "This run reported no table."
                : "No table has reported yet. Rows appear as the extract step reads them."
          }
          testId="entity-progress-grid"
        />
      </div>
      <SkyText size={200} color="muted" block testId="entity-progress-caption">
        Rows extracted is what the source reported for each table. After an extract retry it is the
        highest count any attempt reported. Expected is the row count the extract handed to the
        load, and Rows loaded is what the load wrote. A table whose loaded count differs from
        Expected has already failed its load. The Extract and Load columns are the time each step
        took for that table.
      </SkyText>
    </div>
  );
}
